import { onBeforeUnmount, ref, type Ref } from "vue";
import {
  createRealtimeSession,
  createTranscription,
} from "shared/api/transcription.api";
import {
  startRealtimeTranscription,
  type RealtimeTranscriber,
} from "shared/utils/realtimeTranscriber";

export type VoiceInputErrorKey = "microphoneDenied" | "transcriptionFailed";

export interface UseVoiceInputReturn {
  isSupported: boolean;
  isRecording: Ref<boolean>;
  isTranscribing: Ref<boolean>;
  error: Ref<VoiceInputErrorKey | null>;
  toggle: () => Promise<void>;
}

const FALLBACK_MIME_TYPE = "audio/webm";

/**
 * Hands the microphone over from whichever field is dictating right now.
 *
 * There is one microphone, but a composable per field — and the browser will
 * happily open a second stream while the first is still running, leaving a
 * recording nobody is watching, a second indicator, and two paid sessions. The
 * field that starts takes the microphone off the one before it.
 */
let activeRecording: (() => void) | null = null;

/**
 * Counts hand-overs, so a start that is still opening the microphone can tell
 * that another field has claimed it since.
 *
 * Registering the hand-over only once recording actually begins leaves a window
 * of a second or more — permission prompt, token, handshake — in which a field
 * cannot be taken over yet. Clicking a third field in that window would leave
 * two live recordings, which is the state this is all meant to prevent.
 */
let handOverSequence = 0;

/**
 * MediaRecorder is absent in older Safari, and `navigator.mediaDevices` is
 * undefined on any insecure origin — so this is a real runtime check, not a
 * defensive one. Callers hide the control entirely when it returns false.
 */
const detectSupport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  if (typeof window.MediaRecorder === "undefined") {
    return false;
  }

  return navigator.mediaDevices?.getUserMedia !== undefined;
};

/**
 * Record from the microphone and hand the transcribed text to `onTranscript`.
 *
 * Two paths. The live one streams to Mistral over a WebSocket and calls
 * `onTranscript` with the running transcript while the teacher speaks. When
 * anything about that fails — a school firewall blocking `wss:`, no
 * AudioWorklet, a refused token — it falls back to recording the whole clip and
 * uploading it, which calls `onTranscript` once at the end. `isFinal` tells the
 * two apart: only the final call is worth saving.
 */
export const useVoiceInput = (
  onTranscript: (text: string, isFinal: boolean) => void,
): UseVoiceInputReturn => {
  const isRecording = ref(false);
  const isTranscribing = ref(false);
  const error = ref<VoiceInputErrorKey | null>(null);

  let recorder: MediaRecorder | null = null;
  let transcriber: RealtimeTranscriber | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let mimeType = FALLBACK_MIME_TYPE;
  let liveTranscript = "";
  let isStarting = false;
  let isStopping = false;
  let isUnmounted = false;

  // Read through a call rather than directly: the flag is only ever set from
  // the unmount hook, which TypeScript cannot see, so a second direct read in
  // the same function stays narrowed to `false` after the first one.
  const hasUnmounted = (): boolean => isUnmounted;

  const releaseStream = (): void => {
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
    recorder = null;
  };

  const transcribe = async (): Promise<void> => {
    // Unmounting stops the recorder, which fires onstop and lands here. Without
    // this the audio is still uploaded and the paid transcription still runs,
    // only for the result to be emitted into a component that no longer exists.
    // Clicking away to another student mid-recording is the common case.
    if (isUnmounted === true) {
      chunks = [];
      return;
    }

    if (chunks.length === 0) {
      // A muted or absent input device produces no chunks at all. Staying
      // silent here leaves no text, no spinner and no toast — identical to a
      // button that simply does nothing.
      error.value = "transcriptionFailed";
      isStopping = false;
      return;
    }

    isTranscribing.value = true;

    try {
      const text = await createTranscription(
        new Blob(chunks, { type: mimeType }),
      );
      const trimmed = text.trim();

      if (trimmed.length > 0) {
        onTranscript(trimmed, true);
      }
    } catch {
      error.value = "transcriptionFailed";
    } finally {
      isTranscribing.value = false;
      isStopping = false;
      chunks = [];
    }
  };

  const startRecorder = (): void => {
    recorder = new MediaRecorder(stream as MediaStream);
    mimeType =
      recorder.mimeType.length > 0 ? recorder.mimeType : FALLBACK_MIME_TYPE;

    recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    recorder.onstop = (): void => {
      releaseStream();
      void transcribe();
    };

    recorder.start();
  };

  const start = async (): Promise<void> => {
    // `isRecording` only flips once the microphone and the live session are
    // both up, and the permission prompt alone keeps that pending for seconds.
    // Without a guard held across all of it, a second click opens a second
    // stream and orphans the first one — the microphone stays live for the
    // session, and its socket keeps streaming to a paid endpoint.
    if (isStarting === true) {
      return;
    }

    // Before the microphone is opened, so the previous field has released it.
    activeRecording?.();

    const startedAt = ++handOverSequence;

    isStarting = true;
    error.value = null;
    chunks = [];
    liveTranscript = "";

    // Started before the microphone rather than after it: both take a few
    // hundred milliseconds and neither needs the other, and everything here
    // sits between the click and the button turning red. Swallowing the
    // rejection keeps it from surfacing as an unhandled one when the teacher
    // dismisses the permission prompt.
    const session = createRealtimeSession().catch(() => null);

    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        // Covers both an outright block and the teacher dismissing the prompt.
        error.value = "microphoneDenied";
        return;
      }

      // The teacher may have clicked away while the permission prompt was open.
      if (hasUnmounted() === true) {
        releaseStream();
        return;
      }

      try {
        const resolved = await session;

        if (resolved === null) {
          throw new Error("No realtime session");
        }

        transcriber = await startRealtimeTranscription(stream, resolved, {
          onText: (text: string) => {
            liveTranscript = text;
            onTranscript(text, false);
          },
          onError: () => {
            // A session that dies before it produced a single word is a failed
            // connection, not a failed transcription. The microphone is still
            // open and nothing has been said yet, so drop to recording the clip
            // and uploading it — the same fallback a refused handshake gets —
            // instead of telling the teacher their recording is lost.
            if (liveTranscript.length === 0 && stream !== null) {
              transcriber = null;
              startRecorder();

              return;
            }

            error.value = "transcriptionFailed";

            // The session is gone and every further chunk is discarded, so
            // leaving the button on "recording" would have the teacher talking
            // into nothing.
            if (isRecording.value === true) {
              stop();
            }
          },
        });
      } catch {
        transcriber = null;
        startRecorder();
      }

      // Nothing is holding this session: either the unmount hook already ran
      // and saw a composable that was not yet recording, or another field
      // claimed the microphone while this one was still opening it.
      if (hasUnmounted() === true || startedAt !== handOverSequence) {
        if (transcriber !== null) {
          transcriber.close();
          transcriber = null;
        } else {
          recorder?.stop();
        }

        releaseStream();
        return;
      }

      isRecording.value = true;
      activeRecording = handOverMicrophone;
    } finally {
      isStarting = false;
    }
  };

  const stopRealtime = (immediate: boolean): void => {
    const active = transcriber;
    transcriber = null;

    if (active === null) {
      return;
    }

    releaseStream();

    if (isUnmounted === true) {
      active.close();
      isStopping = false;
      return;
    }

    // Another field wants the microphone. Waiting out the closing transcript
    // would keep this socket and audio context alive beside the new session for
    // seconds, and two live sessions is exactly what breaks the second one —
    // so let go now and keep what is already on screen.
    if (immediate === true) {
      active.close();

      const partial = liveTranscript.trim();

      if (partial.length > 0) {
        onTranscript(partial, true);
      }

      isStopping = false;

      return;
    }

    // The tail of the sentence is still in flight, so the button keeps its
    // spinner until the provider sends the closing text.
    isTranscribing.value = true;

    void active
      .stop()
      .then((text: string) => {
        const trimmed = text.trim();

        if (trimmed.length > 0) {
          onTranscript(trimmed, true);
        }
      })
      .catch(() => {
        error.value = "transcriptionFailed";

        // The field is showing the sentence as it was dictated, but nothing has
        // written it to the model yet — without a closing transcript it looks
        // saved and disappears on the next render.
        const partial = liveTranscript.trim();

        if (partial.length > 0) {
          onTranscript(partial, true);
        }
      })
      // Without this the button keeps its spinner and `isStopping` for the life
      // of the component: every later click returns early and only a reload
      // brings the microphone back.
      .finally(() => {
        isTranscribing.value = false;
        isStopping = false;
      });
  };

  const stop = (immediate = false): void => {
    isRecording.value = false;

    if (activeRecording === handOverMicrophone) {
      activeRecording = null;
    }

    // `onstop` fires asynchronously, and `isTranscribing` only flips once it
    // runs. Without this flag the button renders as an enabled idle microphone
    // in that gap: a click there would start a fresh stream, and the previous
    // recorder's `onstop` would then release *that* stream's tracks, leaving
    // the UI showing "recording" while nothing is captured.
    isStopping = true;

    if (transcriber !== null) {
      stopRealtime(immediate);
      return;
    }

    if (recorder === null) {
      isStopping = false;
      return;
    }

    // Transcription is kicked off from `onstop`, once the last chunk landed.
    recorder.stop();
  };

  /** Registered globally, so identity has to survive the arrow. */
  const handOverMicrophone = (): void => stop(true);

  const toggle = async (): Promise<void> => {
    if (isStopping === true) {
      return;
    }

    if (isRecording.value === true) {
      stop();
      return;
    }

    await start();
  };

  onBeforeUnmount(() => {
    // Set before stop(): stop() triggers onstop, which calls transcribe().
    isUnmounted = true;

    // Navigating away mid-recording must still drop the microphone, otherwise
    // the browser keeps showing its recording indicator for the whole session.
    if (isRecording.value === true) {
      stop();
      return;
    }

    if (activeRecording === handOverMicrophone) {
      activeRecording = null;
    }

    releaseStream();
  });

  return {
    isSupported: detectSupport(),
    isRecording,
    isTranscribing,
    error,
    toggle,
  };
};
